import { CircleSlash, KeyRound } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { RailMaintenanceNotice } from "@/shared/components/payments/RailMaintenanceNotice";
import useTranslations from "@/shared/lang/useTranslations";

import { BREB_KEY_REQUIRED_REASON, RAIL_DISABLED_REASON } from "../../constants/cryptoSwaps.constants";
import type { UseCryptoSwapFormResult } from "../../hooks/useCryptoSwapForm";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSavedWallet } from "../../types/cryptoSwaps.types";
import { NetworkPicker } from "../shared/NetworkPicker";
import { AmountFields, type AmountFieldsBalance } from "./AmountFields";
import { DestinationSection } from "./DestinationSection";

export interface SwapCardProps {
  form: UseCryptoSwapFormResult;
  balance: AmountFieldsBalance;
  /** The staff organization picker, rendered above everything it scopes. */
  organizationSlot?: ReactNode;
  disabled?: boolean;
  /** Offered when the pair is blocked only because the organization has no Bre-B key to refund to. */
  onCreateBrebKey?: () => void;
  savedWallets?: OtcSavedWallet[];
  canSaveWallet?: boolean;
  onManageSavedWallets?: () => void;
  volumeDiscount?: ReactNode;
}

/** How much, then where it lands: one card, not a wizard, since every extra screen spends the countdown. */
export function SwapCard({
  form,
  balance,
  organizationSlot,
  disabled,
  onCreateBrebKey,
  savedWallets,
  canSaveWallet,
  onManageSavedWallets,
  volumeDiscount,
}: SwapCardProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { pair } = form;
  if (!pair) return null;

  return (
    <ColocaSurface padding="none" className="overflow-hidden rounded-3xl">
      {organizationSlot && <div className="border-b border-border p-6">{organizationSlot}</div>}

      {!pair.available && pair.unavailable_reason === RAIL_DISABLED_REASON && (
        <div className="border-b border-border p-6">
          <RailMaintenanceNotice kind="buy" asset={pair.receive_currency} />
        </div>
      )}

      {!pair.available && pair.unavailable_reason !== RAIL_DISABLED_REASON && (
        <div className="border-b border-border p-6">
          <ColocaAlert
            variant="secondary"
            icon={CircleSlash}
            heading={t("pair_unavailable_title")}
            text={labels.unavailableReason(pair.unavailable_reason, pair.receive_currency)}
            actions={
              pair.unavailable_reason === BREB_KEY_REQUIRED_REASON && onCreateBrebKey ? (
                <ColocaButton size="sm" icon={KeyRound} onClick={onCreateBrebKey}>
                  {t("pair_breb_key_create")}
                </ColocaButton>
              ) : undefined
            }
          />
        </div>
      )}

      <div className="border-b border-border p-5 sm:p-6">
        <AmountFields
          pair={pair}
          sendField={form.sendField}
          receiveField={form.receiveField}
          onSendChange={form.setSendAmount}
          onReceiveChange={form.setReceiveAmount}
          sendDecimals={form.sendDecimals}
          receiveDecimals={form.receiveDecimals}
          receiveBounds={form.receiveBounds}
          sendBounds={form.sendBounds}
          sendIssue={form.sendIssue}
          receiveIssue={form.receiveIssue}
          quote={form.quote}
          isPricing={form.isPricing}
          networkLabel={labels.network(form.network)}
          balance={balance}
          debitDiffers={form.debitDiffers}
          disabled={disabled || !form.organizationReady || !form.pairAvailable}
          titleAction={
            form.pairs.length > 1 ? (
              <div className="w-48">
                <ColocaSelect
                  value={pair.pair}
                  onValueChange={form.selectPair}
                  aria-label={t("pair_label")}
                  options={form.pairs.map((candidate) => ({
                    value: candidate.pair,
                    label: `${candidate.send_currency} → ${candidate.receive_currency}`,
                  }))}
                />
              </div>
            ) : undefined
          }
        />
      </div>

      {volumeDiscount && <div className="border-b border-border p-5 sm:p-6">{volumeDiscount}</div>}

      {form.networks.length > 0 && (
        <div className="border-b border-border p-5 sm:p-6">
          <NetworkPicker
            networks={form.networks}
            value={form.network}
            onChange={form.selectNetwork}
            title={t("buy_network_title")}
            hint={t("buy_network_hint")}
            disabled={disabled || !form.organizationReady}
          />
        </div>
      )}

      <div className="p-5 sm:p-6">
        <DestinationSection
          network={form.network}
          asset={pair.receive_currency}
          onChange={form.setDestination}
          disabled={disabled}
          savedWallets={savedWallets}
          canSave={canSaveWallet}
          onManageSaved={onManageSavedWallets}
        />
      </div>
    </ColocaSurface>
  );
}
